<!-- Copyright 2025, Common Good Learning Tools LLC -->
<template>
	<div>
		<k-data-table
			v-if="basic_view"
			:items="filtered_items"
			:headers="basic_headers"
			:options.sync="options"
			hide-default-footer
			class="k-asset-manager-table"
			width="100%"
			fixed-header
			light
		>
			<template v-slot:item="{ item }">
				<tr class="k-table-row" :class="row_class(item)" @click="$emit('show_status', item)">
					<td
						v-for="(header, i) in basic_headers" 
						:key="'header-' + i" 
						:class="[
							{'btn-column': i==0}, 
							{'title-column': i==1}, 
							{'center-text': header.align == 'center'}, 
							{'subject-column': header.value == 'subjects'}, 
							{'nobr-column': header.value == 'publish_status'}
						]"
					>
						<k-icon v-if="header.value == 'button'" class="button" small>fas fa-arrow-up-right-from-square</k-icon>

						<div v-else-if="header.value == 'alignments'" :class="(item.asset.standards.length==0)?'k-asset-manager-table-no-alignments':''">
							{{item.asset.standards.length}}
						</div>
						
						<span v-else v-html="display_value(item, header.value)"></span>
					</td>
				</tr>
			</template>
		</k-data-table>

		<k-data-table
			v-else
			:items="filtered_items"
			:headers="detail_headers"
			:options.sync="options"
			hide-default-footer
			class="k-asset-manager-table"
			width="100%"
			fixed-header
			light
		>
			<template v-slot:item="{ item }">
				<tr class="k-table-row" :class="row_class(item)" @click="$emit('show_status', item)">
					<td
						v-for="(header, i) in detail_headers"
						:key="'header-' + i"
						:class="[
							{'actions-column': header.value == 'actions'},
							{'title-column': header.value == 'title'},
							{'center-text': header.align == 'center'},
							{'subject-column': header.value == 'subjects'},
							{'nobr-column': header.value == 'publish_status'},
							{'id-column-wide': header.value == 'id' && tab.key == 'resource'}
						]"
					>
						<div v-if="header.value == 'alignments'" :class="(item.asset.standards.length == 0) ? 'k-asset-manager-table-no-alignments' : ''">
							{{item.asset.standards.length}}
						</div>

						<template v-else-if="header.value == 'actions'">
							<k-tooltip bottom>
								<template v-slot:activator="{ on }">
									<i v-on="on" class="fas fa-user-magnifying-glass mr-2" style="cursor: pointer;" @click.stop="manage_reviewers(item)"></i>
								</template>
								Manage Reviewers
							</k-tooltip>

							<k-tooltip v-if="site_config.asset_manager.payment_tracking_enabled" bottom>
								<template v-slot:activator="{ on }">
									<i v-on="on" class="fas fa-dollar-sign" style="cursor: pointer;" @click.stop="manage_payments(item)"></i>
								</template>
								Manage Payments
							</k-tooltip>
						</template>

						<span v-else v-html="display_value(item, header.value)"></span>
					</td>
				</tr>
			</template>
		</k-data-table>

		<AssetReviewerDialog
			v-if="managing_reviewers_item"
			:selected_asset="managing_reviewers_item.asset"
			:asset_type="managing_reviewers_item.asset_class_type"
			:payment_data="managing_reviewers_item.payment_data"
			@updated="reviewers_updated"
			@payment_updated="reviewer_payment_updated"
			@done="managing_reviewers_item = null"
		/>

		<AssetPaymentDialog
			v-if="managing_payments_item"
			:selected_asset="managing_payments_item.payment_data"
			@updated="payment_updated"
			@done="managing_payments_item = null"
		/>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import AssetReviewerDialog from './AssetReviewerDialog.vue'
import AssetPaymentDialog from './AssetPaymentDialog.vue'

export default {
	name: 'AssetManagerTable',

	components: {AssetReviewerDialog, AssetPaymentDialog},

	props: {
		tab: {type: Object, required: true},
		headers: {required: true},
		filter: {required: true},
		basic_view: {type: Boolean, required: true},
		payment_data: {type: Array, default: () => null},
		load_payment_data: {type: Function, required: true},
		update_payment_data: {type: Function, required: true},
	},

	data() {
		return {
			init: false,
			items: [],
			options: {
				itemsPerPage: -1,
				sortBy: ['publish_status'],
				sortDesc: [false],
			},
			managing_reviewers_item: null,
			managing_payments_item: null,
		}
	},

	computed: {
		...mapState(['user_info', 'site_config']),

		filtered_items() {
			// filter
			let arr = this.items.filter(x => {
				let include = true

				if (this.filter.term) {
					let re = new RegExp(this.filter.term, 'i')
					let matches_title = x.asset.title().search(re) != -1
					let matches_creator = x.creator_name.search(re) != -1
					let matches_reviewer = !this.basic_view && x.asset.reviewers.some(r => r.assigned_user_name && r.assigned_user_name.search(re) != -1)
					if (!matches_title && !matches_creator && !matches_reviewer) {
						include = false
					}
				}

				if (include && this.filter.status && this.filter.status != 'all') {
					if (x.asset.publish_status != this.filter.status) {
						include = false
					}
				}

				if (include && this.filter.grade) {
					let grade = isNaN(this.filter.grade) ? this.filter.grade : Number(this.filter.grade)
					if (!x.grades.includes(grade)) {
						include = false
					}
				}

				if (include && this.filter.subject) {
					let subjectValues = x.subjects.map(subj => subj.text)
					if (!subjectValues.includes(this.filter.subject)) {
						include = false
					}
				}

				return include
			})

			// then sort
			let sort_by = this.options.sortBy[0] || 'publish_status'
			arr.sort((a,b) => {
				let val = 0

				switch(sort_by) {
					case 'title':
						val = U.natural_sort(a.asset.title(), b.asset.title())
						break
					case 'id': {
						let id_a = this.tab.key == 'lesson' ? a.asset.lesson_id : a.asset.resource_id
						let id_b = this.tab.key == 'lesson' ? b.asset.lesson_id : b.asset.resource_id
						val = U.natural_sort(id_a + '', id_b + '')
						break
					}
					case 'submitted_date':
						if ((a.asset.submitted_date ?? 0 + '') < (b.asset.submitted_date ?? 0 + '')) val = -1	
						else if ((a.asset.submitted_date ?? 0 + '') > (b.asset.submitted_date ?? 0 + '')) val = 1	
						break
					case 'status_date':
						if (a.asset.status_date < b.asset.status_date) val = -1
						else if (a.asset.status_date > b.asset.status_date) val = 1
						break
					case 'reviewer_1':
						val = U.natural_sort(a.asset.reviewers[0]?.assigned_user_name ?? '', b.asset.reviewers[0]?.assigned_user_name ?? '')
						break
					case 'reviewer_2':
						val = U.natural_sort(a.asset.reviewers[1]?.assigned_user_name ?? '', b.asset.reviewers[1]?.assigned_user_name ?? '')
						break
					case 'reviewer_1_approved':
						if ((a.asset.reviewers[0]?.approved_date ?? '') < (b.asset.reviewers[0]?.approved_date ?? '')) val = -1
						else if ((a.asset.reviewers[0]?.approved_date ?? '') > (b.asset.reviewers[0]?.approved_date ?? '')) val = 1
						break
					case 'reviewer_2_approved':
						if ((a.asset.reviewers[1]?.approved_date ?? '') < (b.asset.reviewers[1]?.approved_date ?? '')) val = -1
						else if ((a.asset.reviewers[1]?.approved_date ?? '') > (b.asset.reviewers[1]?.approved_date ?? '')) val = 1
						break
					case 'publish_status': {
						let psa = this.$get_publish_status_data(a.asset.publish_status).index
						let psb = this.$get_publish_status_data(b.asset.publish_status).index
						if (psa != psb) {
							val = psa - psb
						} else {
							// if statuses are the same, go by status_date desc
							if (a.asset.status_date < b.asset.status_date) return 1
							if (a.asset.status_date > b.asset.status_date) return -1
							return 0
						}
						break
					}
					default:
						val = U.natural_sort(a.asset[sort_by], b.asset[sort_by])
				}

				// if we get to here, return val or -1 * val
				return this.options.sortDesc[0] ? -1 * val : val
			})

			return arr
		},

		basic_headers() {
			return this.headers.filter(x => x.basic)
		},

		detail_headers() {
			return this.headers.filter(x => x.value != 'button' && x.show)
		},
	},

	watch: {
		payment_data() {
			this.merge_payment_data()
		},
	},

	async mounted() {
		await this.load_assets()
	},

	methods: {
		async load_assets() {			
			let load_asset_service = () => {
				return new Promise((resolve) => {
					let payload = {
						user_id: this.user_info.user_id,
						asset_type: this.tab.key
					}

					U.ajax('get_unpublished_assets', payload, result => {
						resolve(result)
					})
				})
			}

			U.loading_start()
			let result = await load_asset_service()
			U.loading_stop()

			this.init = true
			if (!result.status == 'ok') {
				console.error('something went wrong: ', result)
			} else if (result.assets) {
				this.items = result.assets.map(x => {
					// store incoming data that doesn't get placed into the standard Resource or Lesson class
					let o = {
						creator_name: x.creator_name,
						creator_email: x.creator_email,
						roles: x.roles,
						subjects: x.subjects,
						grades: x.grades,
						payment_data: null,
					}
					// instantiate the asset class and store some other data
					if (x.lesson_title) {
						o.asset_class_type = 'lesson'
						o.asset = new Lesson(x)
					} else {
						o.asset_class_type = 'resource'
						o.asset = new Resource(x)
					}
					return o
				})

				this.merge_payment_data()
			}
		},

		merge_payment_data() {
			if (!this.payment_data) return

			let lookup = {}
			this.payment_data.forEach(p => { lookup[p.asset_id] = p })

			this.items.forEach(item => {
				let asset_id = item.asset_class_type == 'lesson' ? item.asset.lesson_id : item.asset.resource_id
				item.payment_data = lookup[asset_id] ?? null
			})
		},

		row_class(item) {
			let o = this.$get_publish_status_data(item.asset.publish_status)
			return `${o.color} lighten-5`
		},

		manage_reviewers(item) {
			this.managing_reviewers_item = item
		},

		reviewers_updated(updated_asset) {
			this.managing_reviewers_item.asset = this.managing_reviewers_item.asset_class_type == 'lesson' ? new Lesson(updated_asset) : new Resource(updated_asset)
		},

		// fired by AssetReviewerDialog when a reviewer is added/removed and a payment record already exists for this asset, so its reviewer_data stays in sync
		reviewer_payment_updated(payment_data) {
			this.$store.dispatch('save_asset_review_payment', {user_id: this.user_info.user_id, payment_data})
				.then(() => {
					this.managing_reviewers_item.payment_data = payment_data
					this.update_payment_data(payment_data)
				})
		},

		async manage_payments(item) {
			// load payment data if it hasn't been loaded yet (e.g. via the header $ button)
			if (!this.payment_data) {
				await this.load_payment_data()
				this.merge_payment_data()
			}

			// if this asset doesn't have a payment record yet, create one now (mirrors AssetPublishStatus.vue's create_payment_record)
			if (!item.payment_data) {
				item.payment_data = await this.create_payment_record(item)
				this.update_payment_data(item.payment_data)
			}

			this.managing_payments_item = item
		},

		create_payment_record(item) {
			let payment_data = {
				asset_id: item.asset_class_type == 'lesson' ? item.asset.lesson_id : item.asset.resource_id,
				asset_title: item.asset.title(),
				asset_type: this.tab.key,
				author_id: item.asset_class_type == 'lesson' ? item.asset.creator_user_id : item.asset.creator,
				submitted_date: item.asset.submitted_date || '*CLEAR*',
				author_name: item.creator_name,
				publish_date: '*CLEAR*',
				reviewer_data: item.asset.reviewers.map(reviewer => ({
					user_id: reviewer.assigned_user_id,
					user_name: reviewer.assigned_user_name,
					approved_date: reviewer.approved ? reviewer.approved_date : null,
					payments: [],
				})),
			}

			return this.$store.dispatch('save_asset_review_payment', {user_id: this.user_info.user_id, payment_data})
				.then(result => result.payment_data)
		},

		payment_updated(updated_payment_data) {
			this.managing_payments_item.payment_data = updated_payment_data
			this.update_payment_data(updated_payment_data)
		},

		display_value(item, key) {
			let value = item[key]

			/* Handle date values */
			if (key.includes('date') || key.includes('_at')) {
				return this.date_value(item.asset[key])
			} else if (key.includes('reviewer')) {
				/* Handle reviewer values */
				let reviewer_1 = item.asset.reviewers[0] ?? {}
				let reviewer_2 = item.asset.reviewers[1] ?? {}
				let others = item.asset.reviewers.slice(2)
				switch (key) {
					case 'reviewer_1':
						return this.user_name_value(item, reviewer_1)
					case 'reviewer_1_approved':
						return this.date_value(reviewer_1.approved_date)
					case 'reviewer_2':
						return this.user_name_value(item, reviewer_2)
					case 'reviewer_2_approved':
						return this.date_value(reviewer_2.approved_date)
					case 'other_reviewers':
						return others.map(x => this.user_name_value(item, x)).join(', ')
					default:
						return key
				}
			} else {
				/* Handle other values */
				switch (key) {
					case 'publish_status': {
						let o = this.$get_publish_status_data(item.asset.publish_status)

						let output = `<span class="${o.color}--text text--darken-4"><i class="${o.icon}"></i> ${o.text}`

						if (!this.basic_view) {
							let approvals = item.asset.reviewers.filter(x => x.approved).length
							let total = item.asset.reviewers.length
							output += ` <span style="font-size:11px">(${approvals}/${total})</span>`

							if (item.asset.review_note && (item.roles.includes('reviewer') || item.roles.includes('publisher'))) {
								let note = item.asset.review_note
								if (note.length > 30) {
									let note_array = note.split(' ')
									note = note_array.splice(0, 10).join(' ') + '...'
								}
								output += `<i class="fas fa-file-pen ml-1 tooltip"><span class="tooltip-text">${note}</span></i>`
							}
						}

						output += `</span>`

						return output
					}
					case 'creator_name':
						return this.user_name_value(item)
					case 'subjects':
						return value.map(x => x.text).join(', ')
					case 'grades':
						return value.join(', ')
					case 'title':
						return `<i class="mr-1 fas ${item.asset.icon()}"></i>${item.asset.title()}`
					case 'id':
						return this.tab.key == 'lesson' ? item.asset.lesson_id : item.asset.resource_id
					default:
						return value ? value : key
				}
			}
		},

		// pass a reviewer to render a reviewer's name, or omit it to render the item's author (creator) name
		user_name_value(item, reviewer) {
			let name = reviewer ? reviewer.assigned_user_name : item.creator_name
			if (!name) return ''

			let name_html = reviewer?.active
				? `<span class="green--text"><i class="fas fa-user-check mr-1"></i>${name}</span>`
				: name

			if (item.payment_data && !this.basic_view) {
				let payments = reviewer
					? item.payment_data.reviewer_data?.find(x => x.user_id == reviewer.assigned_user_id)?.payments
					: item.payment_data.author_payments
				name_html += ` <span style="font-size:11px">(${this.payment_amount_text(payments)})</span>`
			}

			return name_html
		},

		payment_amount_text(payments) {
			if (empty(payments)) return '$0'
			let amount = 0
			payments.forEach(x => { amount += parseInt(x.amount) })
			return '$' + amount
		},

		date_value(value) {
			if (empty(value)) return ''
			let date = new Date(value)
			if (date != 'Invalid Date'){
				return date.toLocaleDateString()	// this renders the date in the local timezone
			} else {
				return value
			}
		},
	},
}
</script>

<style lang="scss">
.k-asset-manager-table {
	height: 100%;
	width: 100%;

	td, th {
		font-size:12px!important;
		line-height:15px;
	}
	th {
		white-space: nowrap;
		height:32px!important;
	}

	.k-table-row {
		cursor: pointer;
		.button {
			cursor: pointer;
		}

		.btn-column {
			width:5px;
			padding-left:8px!important;
			padding-right:0!important;
		}
		.actions-column {
			width: 50px;
			white-space: nowrap;
		}
		.title-column {
			width: 340px;
		}
		.subject-column {
			max-width:160px;
		}
		.nobr-column {
			white-space:nowrap;
		}
		.id-column-wide {
			min-width: 260px;
		}

		.center-text {
			text-align: center;
		}

		.tooltip {
			position: relative;
			display: inline-block;
		}

		/* Tooltip text */
		.tooltip .tooltip-text {
			visibility: hidden;
			bottom: 15px;
			left: -15px;
			background-color: black;
			color: #fff;
			text-align: center;
			padding: 5px 6px;
			border-radius: 6px;
			position: absolute;
			z-index: 100;
			font-family: 'Helvetica Neue, Helvetica, Arial, sans-serif';
		}

		/* Show the tooltip text when you mouse over the tooltip container */
		.tooltip:hover .tooltip-text {
			visibility: visible;
		}
	}

	.k-asset-manager-table-no-alignments {
		font-weight: bold;
		color: $v-red-darken-3;
	}
}
</style>